import { usePutOrder } from '@api/pos/pos-transaction-api.rtk';
import useToast from '@hooks/useToast';
import { IPosOrder } from '@modules/point-of-sale/interface/orders/pos-orders.interface';
import {
  selectPOSstationID,
  selectPOSTableOrders,
} from '@modules/point-of-sale/slice/pos-selector';
import {
  isSamePOSOrderLine,
  updateTableOrder,
} from '@modules/point-of-sale/slice/pos-table-area.slice';
import { Add, Chat, Edit, Remove } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { formatNumber } from '@utils/format';
import { buildPutOrderPayload } from '@utils/pos-utils';
import { usePOSScreen } from '@utils/screen-size';
import { memo, useEffect, useMemo, useState } from 'react';
import { getColor } from '../../../../../assets/theme';
import { usePOSOrderDetailsCtx } from './pos-order-taking-window';

/**
 * Props for the POSOrderItem component.
 *
 * @typedef {Object} POSOrderItemProps
 * @property {POSMenuProduct} item - The item to be displayed.
 * @property {[POSMenuProduct[], React.Dispatch<React.SetStateAction<POSMenuProduct[]>>]} editItemsState - State for managing edited items.
 * @property {() => void} openRemoveWarning - Function to open the remove confirmation dialog.
 */

/**
 * POSOrderItem component displays an individual item in the POS order.
 * Allows users to edit item quantity, add remarks, and remove items.
 *
 * @param {POSOrderItemProps} props - The props for the component.
 * @returns {JSX.Element} The rendered component.
 */
const POSOrderItem = ({
  item,
  editItemsState,
  openRemoveWarning,
}: {
  item: IPosOrder;
  editItemsState: [
    IPosOrder[],
    React.Dispatch<React.SetStateAction<IPosOrder[]>>,
  ];
  openRemoveWarning: () => void;
}) => {
  const { isBillOut } = usePOSOrderDetailsCtx();
  const { primary, lightGray, danger, btnBG, iconBG } = getColor();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const orders = useAppSelector(selectPOSTableOrders);
  const propertyID = useAppSelector(selectPropertyID);
  const stationID = useAppSelector(selectPOSstationID);
  const [putOrder, { isLoading }] = usePutOrder();
  const isPOS1024p = usePOSScreen();

  // #region ITEM DETAILS
  const {
    description: itemName,
    quantity: initialQuantity,
    lineRemarks: itemRemarks,
    isPrintedOS: initialIsPrintedOS,
    grossAmount,
    orderID,
    discountTotal,
    discountTitle,
    netAmount,
  } = item;

  const hasDiscount = useMemo(() => discountTotal > 0, [discountTotal]);

  const isPrintedOS = useMemo(
    () => Boolean(initialIsPrintedOS),
    [initialIsPrintedOS],
  );

  const initialPrice = useMemo(
    () => grossAmount / initialQuantity,
    [initialQuantity, grossAmount, orderID],
  );

  const itemQuantity = useMemo(
    () => parseInt(initialQuantity?.toString()),
    [initialQuantity],
  );

  // #endregion

  // #region EDIT / REMOVE / VOID DETAILS
  const [remarks, setRemarks] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [onEdit, setOnEdit] = useState(false);
  const [editItems, setEditItems] = editItemsState;

  // Check if the item is currently being edited
  const isItemOnEdit = useMemo(
    () => editItems.some((editItem) => isSamePOSOrderLine(editItem, item)),
    [editItems, item],
  );

  // Determine if the item is not selectable
  const notSelectable = useMemo(() => {
    if (editItems.length === 0) return false;

    const firstIsPrintedOrHasOrder = Boolean(
      editItems[0]?.isPrintedOS,
      // editItems[0]?.isPrintedOS || editItems[0]?.orderID,
    );

    const currentIsPrinted = Boolean(isPrintedOS);

    return firstIsPrintedOrHasOrder !== currentIsPrinted;

    // const hasOrderID = !!orderID;

    // if (firstIsPrintedOrHasOrder) {
    //   return !hasOrderID;
    // }

    // return currentIsPrinted !== false || hasOrderID;
  }, [editItems, isPrintedOS, orderID]);

  // Handle item click to add/remove from edit items
  const onClickItems = () => {
    setEditItems((prev) => {
      const exists = prev.some((prevItem) =>
        isSamePOSOrderLine(prevItem, item),
      );

      if (exists) {
        // remove that specific item
        return prev.filter((prevItem) => !isSamePOSOrderLine(prevItem, item));
      }

      // otherwise add it
      return [...prev, item];
    });
  };

  // Handle input change for quantity
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 9999) {
      setQuantity(value);
    }
  };

  // Increment quantity
  const handleIncrement = (e) => {
    e.stopPropagation();
    setQuantity((prevQuantity) => Math.min(prevQuantity + 1, 9999));
  };

  // Decrement quantity
  const handleDecrement = (e) => {
    e.stopPropagation();
    setQuantity((prevQuantity) => Math.max(prevQuantity - 1, 1));
  };

  // Save the edited item
  const handleSave = async () => {
    try {
      if (item.orderID !== '') {
        const { orderID, transactionID } = item;

        await putOrder({
          ids: { orderID, transactionID },
          payload: {
            properties: [
              {
                property: {
                  propertyID,
                  posOrders: [
                    buildPutOrderPayload({
                      order: item,
                      propertyID,
                      stationID,
                      quantity,
                      lineRemarks: remarks,
                    }),
                  ],
                },
              },
            ],
          },
        }).unwrap();
      } else {
        dispatch(
          updateTableOrder({
            ...item,
            quantity: quantity,
            lineRemarks: remarks,
            grossAmount: initialPrice * quantity,
            unitPrice: initialPrice,
            previousUnitPrice: item.unitPrice,
            previousLineRemarks: item.lineRemarks,
          }),
        );
      }

      setOnEdit(false);
      setEditItems((prev) =>
        prev.filter((prevItem) => !isSamePOSOrderLine(prevItem, item)),
      );
      toast({
        message: `${itemName} successfully updated!`,
        severity: 'success',
      });
    } catch (error) {
      toast({ message: error, severity: 'error' });
    }
  };
  // #endregion

  // Reset edit mode when edit items change
  useEffect(() => {
    if (editItems?.length <= 0) {
      setOnEdit(false);
    }
  }, [editItems]);

  return (
    <Stack
      sx={{
        bgcolor: notSelectable
          ? '#0000001f'
          : isItemOnEdit
            ? '#e7e7f5ff'
            : !orderID
              ? '#fff3e694'
              : 'transparent',
        cursor: notSelectable
          ? 'not-allowed'
          : !isBillOut
            ? 'pointer'
            : 'default',
        '&:hover': {
          bgcolor: notSelectable
            ? ''
            : isItemOnEdit
              ? '#e7e7f5ff'
              : '#f4f4f9ff',
        },
        position: 'relative',
      }}
    >
      <Box
        sx={{
          // LINE UI
          position: 'absolute',
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: 4,
          height: '100%',
          bgcolor: isPrintedOS
            ? 'success.main'
            : orderID
              ? 'secondary.main'
              : '',

          // TAG UI
          // width: 0,
          // height: 0,
          // borderTopWidth: '12px',
          // borderTopStyle: 'solid',
          // borderTopColor: isPrintedOS
          //   ? 'success.main'
          //   : orderID
          //     ? 'secondary.main'
          //     : 'transparent',
          // borderRight: '12px solid transparent',
        }}
      />
      <Stack
        onClick={
          onEdit || notSelectable || isBillOut ? undefined : onClickItems
        }
        sx={{
          gap: 2,
          px: 1.5,
          py: 1,
          position: 'relative',
          justifyContent: 'center',
          maxHeight: onEdit ? '100%' : '56px',
        }}
      >
        <Stack direction='row' sx={{ gap: 2, justifyContent: 'space-between' }}>
          <Stack direction='row' sx={{ alignItems: 'center', gap: 1 }}>
            {onEdit ? (
              <Stack
                direction='row'
                sx={{
                  borderRadius: '32px',
                  bgcolor: btnBG,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: isPOS1024p ? 0 : 1,
                  px: isPOS1024p ? 0.8 : 1.3,
                }}
              >
                <IconButton
                  onClick={handleDecrement}
                  size={isPOS1024p ? 'small' : 'medium'}
                  sx={{
                    bgcolor: primary,
                    color: '#FFF',
                    ':hover': {
                      bgcolor: `${primary}90`,
                    },
                    '&.Mui-disabled': {
                      bgcolor: iconBG,
                      color: '#FFF',
                    },
                  }}
                >
                  <Remove />
                </IconButton>
                <TextField
                  value={quantity}
                  onChange={handleInputChange}
                  variant='outlined'
                  size='small'
                  inputProps={{
                    style: {
                      textAlign: 'center',
                      width: isPOS1024p ? 30 : 40,
                      fontSize: '16px',
                    },
                    min: 1,
                    max: 9999,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: 'transparent',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'transparent',
                      },
                    },
                  }}
                />
                <IconButton
                  onClick={handleIncrement}
                  disabled={itemQuantity === 99}
                  size={isPOS1024p ? 'small' : 'medium'}
                  sx={{
                    bgcolor: primary,
                    color: '#FFF',
                    ':hover': {
                      bgcolor: `${primary}90`,
                    },
                    '&.Mui-disabled': {
                      bgcolor: iconBG,
                      color: '#FFF',
                    },
                  }}
                >
                  <Add />
                </IconButton>
              </Stack>
            ) : (
              <Typography
                sx={{
                  px: 2,
                  minWidth: '5ch',
                  color: notSelectable ? '#00000042' : 'default',
                }}
              >
                {itemQuantity}
              </Typography>
            )}

            <Stack
              flexDirection='row'
              flexWrap='wrap'
              justifyContent='space-between'
              component='span'
              sx={{
                color: notSelectable ? '#00000042' : 'default',
                position: 'relative',
              }}
            >
              <Typography sx={{ pr: 1.5 }} textOverflow='ellipsis'>
                {itemName}
              </Typography>

              {discountTitle && (
                <Chip
                  label={`${discountTitle}`}
                  size='small'
                  sx={{
                    bgcolor: isItemOnEdit ? 'inherit' : '#fff0d9ff',
                    border: `1px solid ${isItemOnEdit ? 'gray' : 'inherit'}`,
                    ml: -1,
                    mt: -0.2,
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              sx={{
                color: notSelectable ? '#00000042' : 'default',
                ...(hasDiscount && {
                  textDecoration: 'line-through',
                  opacity: 0.6,
                }),
                fontWeight: 500,
              }}
            >
              {formatNumber(
                onEdit ? initialPrice * quantity : grossAmount,
                2,
                2,
              )}
            </Typography>
            {hasDiscount && (
              <Typography
                sx={{
                  color: notSelectable ? '#00000042' : 'default',
                  fontWeight: 500,
                }}
              >
                {formatNumber(netAmount, 2, 2)}
              </Typography>
            )}
            {itemRemarks && (
              <Tooltip title={itemRemarks}>
                <Chat sx={{ color: primary, cursor: 'default' }} />
              </Tooltip>
            )}
            {!hasDiscount && !isPrintedOS && isItemOnEdit && !onEdit && (
              <IconButton
                size='small'
                sx={{
                  p: isPOS1024p ? 0 : 0.5,
                  bgcolor: lightGray,
                  ':hover': {
                    bgcolor: primary,
                    color: '#FFF',
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const isExist = orders?.find((pItem) =>
                    isSamePOSOrderLine(pItem, item),
                  );

                  setRemarks('');
                  setQuantity(1);
                  if (isExist?.lineRemarks) {
                    setRemarks(isExist.lineRemarks);
                  }
                  if (isExist?.quantity && isExist?.quantity > 0) {
                    setQuantity(parseInt(isExist.quantity?.toString()));
                  }
                  setOnEdit(true);
                }}
              >
                <Edit />
              </IconButton>
            )}
          </Stack>
        </Stack>
      </Stack>
      <Collapse in={onEdit}>
        <Stack gap={2} p={2} pt={1}>
          <TextField
            multiline
            rows={3}
            label='Remarks'
            InputLabelProps={{
              shrink: true,
            }}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
          />
          <Stack direction='row' justifyContent='space-between'>
            {!item.isPrintedOS && !orderID && (
              <Button
                size={isPOS1024p ? 'small' : 'medium'}
                sx={{ color: danger }}
                onClick={(e) => {
                  e.stopPropagation();
                  openRemoveWarning();
                }}
              >
                Remove
              </Button>
            )}
            <Stack direction='row' gap={1} ml='auto'>
              <Button
                size={isPOS1024p ? 'small' : 'medium'}
                variant='outlined'
                onClick={(e) => {
                  e.stopPropagation();
                  setOnEdit(false);
                }}
              >
                Close
              </Button>
              <LoadingButton
                size={isPOS1024p ? 'small' : 'medium'}
                variant='contained'
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                loading={isLoading}
              >
                Save
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
      </Collapse>
      <Divider
        sx={{
          borderStyle: 'dashed',
          borderWidth: '1px',
        }}
      />
    </Stack>
  );
};

export default memo(POSOrderItem);
